import { z } from 'zod';

import { AUTH_LAST_SIGN_IN_ACCOUNT_KEY, AUTH_SHARED_COMPUTER_KEY } from './constants';
import { getStorageItem, removeStorageItem, replaceStorageItem } from './storage';

// The account remembered here is only a hint for Google's account chooser; it grants no access.
const SignInAccountSchema = z.string().email();

// Returns null when nothing is stored, the stored value is not an email address, or storage cannot be read.
export const getRememberedSignInAccount = (): string | null => {
  const parsed = SignInAccountSchema.safeParse(getStorageItem(AUTH_LAST_SIGN_IN_ACCOUNT_KEY));
  return parsed.success ? parsed.data : null;
};

// Replaces the remembered account. A value that is not an email address is ignored and the stored one is kept.
export const rememberSignInAccount = (email: string): void => {
  const parsed = SignInAccountSchema.safeParse(email);
  if (parsed.success) {
    replaceStorageItem(AUTH_LAST_SIGN_IN_ACCOUNT_KEY, parsed.data);
  }
};

export const forgetSignInAccount = (): void => {
  removeStorageItem(AUTH_LAST_SIGN_IN_ACCOUNT_KEY);
};

// The choice belongs to the computer, not to a person, so it is kept until someone unchecks it.
export const isSharedComputer = (): boolean => getStorageItem(AUTH_SHARED_COMPUTER_KEY) === 'true';

// Marking the computer as shared also forgets the remembered account right away.
export const setSharedComputer = (isShared: boolean): void => {
  if (isShared) {
    replaceStorageItem(AUTH_SHARED_COMPUTER_KEY, 'true');
    forgetSignInAccount();
  } else {
    removeStorageItem(AUTH_SHARED_COMPUTER_KEY);
  }
};

// Sign-out wipes localStorage, but the remembered account and the shared computer choice must survive it.
export const clearStorageKeepingSignInPreferences = (): void => {
  const remembered = getRememberedSignInAccount();
  const isShared = isSharedComputer();
  try {
    localStorage.clear();
  } catch (_error) {}
  if (remembered) {
    rememberSignInAccount(remembered);
  }
  setSharedComputer(isShared);
};
